import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ClipboardCheck, Plus, CheckCircle2, XCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const statusColor = (s) => ({
  Created: 'bg-blue-100 text-blue-700',
  'In Inspection': 'bg-amber-100 text-amber-700',
  Completed: 'bg-green-100 text-green-700',
}[s] || 'bg-gray-100');
const resultColor = (r) => ({
  Accepted: 'text-green-700', Rejected: 'text-red-600', Pending: 'text-gray-400',
}[r] || '');
const NEW_LOT = { material: '', plant: '1000', storage_location: '0001', lot_quantity: '', inspection_type: '', plan_number: '', post_stock: true };

const InspectionLots = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('inspection_lot');
  const [lots, setLots] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(NEW_LOT);
  const [saving, setSaving] = useState(false);
  const [lot, setLot] = useState(null); // detail dialog
  const [results, setResults] = useState([]);
  const [ud, setUd] = useState({ decision: 'Accept', accepted_quantity: '', rejected_quantity: '', remarks: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/quality/inspection-lots`, { withCredentials: true });
      setLots(res.data);
    } catch (e) { toast.error('Failed to load inspection lots'); }
    finally { setLoading(false); }
  }, []);
  const loadTypes = useCallback(async () => {
    try { const res = await axios.get(`${API}/quality/inspection-types`, { withCredentials: true }); setTypes(res.data); }
    catch (e) { /* non-blocking */ }
  }, []);

  useEffect(() => { load(); loadTypes(); }, [load, loadTypes]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const create = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/quality/inspection-lots`, {
        ...form, lot_quantity: parseFloat(form.lot_quantity),
        inspection_type: form.inspection_type || null, plan_number: form.plan_number || null,
      }, { withCredentials: true });
      toast.success('Inspection lot created'); setCreateOpen(false); setForm(NEW_LOT); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create lot'); }
    finally { setSaving(false); }
  };

  const openLot = async (lotNumber) => {
    try {
      const res = await axios.get(`${API}/quality/inspection-lots/${lotNumber}`, { withCredentials: true });
      setLot(res.data);
      setResults(res.data.results.map((r) => ({ ...r })));
      setUd({ decision: 'Accept', accepted_quantity: res.data.lot_quantity, rejected_quantity: 0, remarks: '' });
    } catch (e) { toast.error('Failed to open lot'); }
  };

  const setResult = (i, k, v) => setResults((p) => { const a = [...p]; a[i] = { ...a[i], [k]: v }; return a; });

  const saveResults = async () => {
    setBusy(true);
    try {
      const payload = {
        results: results.map((r) => ({
          characteristic_name: r.characteristic_name, quantitative: r.quantitative,
          measured_value: r.measured_value === '' || r.measured_value == null ? null : parseFloat(r.measured_value),
          qualitative_result: r.qualitative_result || null,
          result: r.result && r.result !== 'Pending' ? r.result : 'Pending', remarks: r.remarks || '',
        })),
      };
      const res = await axios.post(`${API}/quality/inspection-lots/${lot.lot_number}/results`, payload, { withCredentials: true });
      setLot(res.data); setResults(res.data.results.map((r) => ({ ...r })));
      toast.success('Results recorded'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to record results'); }
    finally { setBusy(false); }
  };

  const submitUD = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/quality/inspection-lots/${lot.lot_number}/usage-decision`, {
        decision: ud.decision,
        accepted_quantity: parseFloat(ud.accepted_quantity) || 0,
        rejected_quantity: parseFloat(ud.rejected_quantity) || 0,
        remarks: ud.remarks,
      }, { withCredentials: true });
      setLot(res.data); toast.success('Usage decision recorded'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Usage decision failed'); }
    finally { setBusy(false); }
  };

  const onDecisionChange = (v) => {
    if (!lot) return setUd((p) => ({ ...p, decision: v }));
    if (v === 'Accept') setUd({ decision: v, accepted_quantity: lot.lot_quantity, rejected_quantity: 0, remarks: ud.remarks });
    else if (v === 'Reject') setUd({ decision: v, accepted_quantity: 0, rejected_quantity: lot.lot_quantity, remarks: ud.remarks });
    else setUd((p) => ({ ...p, decision: v }));
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="inspection-lots-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-teal-100 text-teal-700"><ClipboardCheck className="h-5 w-5" /></div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Inspection Lots</h1>
              <p className="text-gray-600 text-sm mt-0.5">Record results and make usage decisions.</p>
            </div>
          </div>
          {writable && <Button onClick={() => setCreateOpen(true)} data-testid="new-lot-btn"><Plus className="h-4 w-4 mr-2" />New Inspection Lot</Button>}
        </div>

        <Card className="p-0 overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Lot #</TableHead><TableHead>Material</TableHead><TableHead>Plant</TableHead>
              <TableHead className="text-right">Qty</TableHead><TableHead>Plan</TableHead><TableHead>Status</TableHead>
              <TableHead>UD</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-500">Loading...</TableCell></TableRow>
                : lots.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-500">No inspection lots yet.</TableCell></TableRow>
                : lots.map((l) => (
                  <TableRow key={l.lot_number} data-testid={`lot-row-${l.lot_number}`}>
                    <TableCell className="font-medium">{l.lot_number}</TableCell>
                    <TableCell>{l.material}</TableCell>
                    <TableCell>{l.plant}</TableCell>
                    <TableCell className="text-right">{l.lot_quantity}</TableCell>
                    <TableCell>{l.plan_number || '—'}</TableCell>
                    <TableCell><span className={`px-2 py-1 rounded text-xs font-medium ${statusColor(l.status)}`}>{l.status}</span></TableCell>
                    <TableCell>{l.usage_decision || '—'}</TableCell>
                    <TableCell><Button size="sm" variant="outline" onClick={() => openLot(l.lot_number)} data-testid={`inspect-lot-${l.lot_number}`}>Inspect</Button></TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Inspection Lot</DialogTitle>
            <DialogDescription>Characteristics are pulled from the material's active inspection plan.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2"><Label>Material</Label><Input data-testid="lot-material" value={form.material} onChange={(e) => set('material', e.target.value)} /></div>
            <div><Label>Plant</Label><Input data-testid="lot-plant" value={form.plant} onChange={(e) => set('plant', e.target.value)} /></div>
            <div><Label>Storage Location</Label><Input data-testid="lot-storage" value={form.storage_location} onChange={(e) => set('storage_location', e.target.value)} /></div>
            <div><Label>Lot Quantity</Label><Input type="number" data-testid="lot-qty" value={form.lot_quantity} onChange={(e) => set('lot_quantity', e.target.value)} /></div>
            <div>
              <Label>Inspection Type</Label>
              <Select value={form.inspection_type} onValueChange={(v) => set('inspection_type', v)}>
                <SelectTrigger data-testid="lot-type"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>{types.map((t) => <SelectItem key={t.code} value={t.code}>{t.code} — {t.description}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2 flex items-center gap-2 rounded-lg border p-3">
              <Checkbox id="post_stock" data-testid="lot-post-stock" checked={form.post_stock} onCheckedChange={(v) => set('post_stock', !!v)} />
              <Label htmlFor="post_stock" className="cursor-pointer">Post quantity into Quality inspection stock</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={saving || !form.material || !form.lot_quantity || !form.plant} data-testid="save-lot-btn">{saving ? 'Creating...' : 'Create Lot'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail / results / UD dialog */}
      <Dialog open={!!lot} onOpenChange={(o) => !o && setLot(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          {lot && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">{lot.lot_number}
                  <span className={`px-2 py-1 rounded text-xs font-medium ${statusColor(lot.status)}`} data-testid="lot-detail-status">{lot.status}</span>
                </DialogTitle>
                <DialogDescription>{lot.material} · {lot.plant} · Qty {lot.lot_quantity} {lot.stock_posted ? '· stock in quality' : ''}</DialogDescription>
              </DialogHeader>

              {results.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Characteristic</TableHead><TableHead>Spec</TableHead>
                      <TableHead>Measured / Result</TableHead><TableHead>Outcome</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {results.map((r, i) => {
                        const char = lot.characteristics.find((c) => c.name === r.characteristic_name) || {};
                        const completed = lot.status === 'Completed';
                        return (
                          <TableRow key={i} data-testid={`result-row-${i}`}>
                            <TableCell className="font-medium">{r.characteristic_name}</TableCell>
                            <TableCell className="text-xs text-gray-500">
                              {r.quantitative ? `${char.lower_limit ?? '−∞'} … ${char.upper_limit ?? '∞'} ${char.unit || ''}` : 'Qualitative'}
                            </TableCell>
                            <TableCell>
                              {r.quantitative ? (
                                <Input type="number" className="h-8 w-28" disabled={completed} data-testid={`result-value-${i}`}
                                  value={r.measured_value ?? ''} onChange={(e) => setResult(i, 'measured_value', e.target.value)} />
                              ) : (
                                <select className="h-8 rounded-md border border-gray-200 text-sm px-2" disabled={completed}
                                  data-testid={`result-qual-${i}`}
                                  value={r.result === 'Pending' ? '' : r.result}
                                  onChange={(e) => { setResult(i, 'result', e.target.value); setResult(i, 'qualitative_result', e.target.value); }}>
                                  <option value="">—</option>
                                  <option value="Accepted">OK</option>
                                  <option value="Rejected">Not OK</option>
                                </select>
                              )}
                            </TableCell>
                            <TableCell className={`font-medium ${resultColor(r.result)}`} data-testid={`result-outcome-${i}`}>{r.result}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-sm text-gray-500">No characteristics — this lot has no linked inspection plan.</p>
              )}

              {writable && lot.status !== 'Completed' && results.length > 0 && (
                <Button variant="outline" onClick={saveResults} disabled={busy} data-testid="save-results-btn">Record Results</Button>
              )}

              {/* Usage decision */}
              {lot.status === 'Completed' ? (
                <Card className="p-4 bg-gray-50">
                  <div className="flex items-center gap-2 font-medium">
                    {lot.usage_decision === 'Reject' ? <XCircle className="h-5 w-5 text-red-600" /> : <CheckCircle2 className="h-5 w-5 text-green-600" />}
                    Usage Decision: {lot.usage_decision}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Accepted {lot.accepted_quantity} · Rejected {lot.rejected_quantity} · by {lot.decided_by}</div>
                </Card>
              ) : writable && (
                <Card className="p-4 border-teal-100">
                  <h4 className="font-semibold text-gray-900 mb-3">Usage Decision</h4>
                  <div className="grid grid-cols-3 gap-3 items-end">
                    <div>
                      <Label>Decision</Label>
                      <Select value={ud.decision} onValueChange={onDecisionChange}>
                        <SelectTrigger data-testid="ud-decision"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Accept">Accept</SelectItem>
                          <SelectItem value="Reject">Reject</SelectItem>
                          <SelectItem value="Partial">Partial</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Accepted Qty</Label><Input type="number" data-testid="ud-accepted" value={ud.accepted_quantity} onChange={(e) => setUd({ ...ud, accepted_quantity: e.target.value })} /></div>
                    <div><Label>Rejected Qty</Label><Input type="number" data-testid="ud-rejected" value={ud.rejected_quantity} onChange={(e) => setUd({ ...ud, rejected_quantity: e.target.value })} /></div>
                    <div className="col-span-3"><Label>Remarks</Label><Input data-testid="ud-remarks" value={ud.remarks} onChange={(e) => setUd({ ...ud, remarks: e.target.value })} /></div>
                  </div>
                  <Button className="mt-3" onClick={submitUD} disabled={busy} data-testid="submit-ud-btn">Post Usage Decision</Button>
                </Card>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default InspectionLots;
